import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { JoinUsSubmitApi } from '../../apis/api/JoinUsApi';
import { resetNMMStatus } from '../../apis/slice/JoinUsSlice';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import dayjs from 'dayjs';
import './JoinUs.css';
import { fetchCountryCodeListApi, fetchCountryListApi } from '../../apis/api/UtilsApi';





const EMPLOYEE_OPTIONS = [
  { value: '1-10', label: '1 – 10' },
  { value: '11-50', label: '11 – 50' },
  { value: '51-200', label: '51 – 200' },
  { value: '201-500', label: '201 – 500' },
  { value: '500+', label: '500+' },
];

const REVENUE_OPTIONS = [
  { value: 'under-100k', label: 'Under USD 100,000' },
  { value: '100k-500k', label: 'USD 100,000 – 500,000' },
  { value: '500k-1m', label: 'USD 500,000 – 1,000,000' },
  { value: '1m-5m', label: 'USD 1M – 5M' },
  { value: '5m+', label: 'Over USD 5M' },
];

const VOLUME_OPTIONS = [
  { value: 'under-50', label: 'Under 50 shipments/month' },
  { value: '50-200', label: '50 – 200 shipments/month' },
  { value: '200-500', label: '200 – 500 shipments/month' },
  { value: '500+', label: '500+ shipments/month' },
];

const SERVICES_OPTIONS = [
  { value: 'air-freight', label: 'Air Freight' },
  { value: 'sea-freight', label: 'Sea Freight' },
  { value: 'road-transport', label: 'Road Transport' },
  { value: 'customs-brokerage', label: 'Customs Brokerage' },
  { value: 'warehousing', label: 'Warehousing & Distribution' },
  { value: 'consolidation', label: 'Consolidation / LCL' },
  { value: 'project-cargo', label: 'Project Cargo' },
  { value: 'dangerous-goods', label: 'Dangerous Goods' },
  { value: 'other', label: 'Other' },
];

const PLAN_OPTIONS = [
  { value: 'annual', label: 'Annual Membership — USD 1,200 / year' },
  { value: '2year', label: '2 Year Membership — USD 2,040' },
  { value: '3year', label: '3 Year Membership — USD 2,880' },
];

const HOW_OPTIONS = [
  { value: 'google', label: 'Google / Search Engine' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'referral', label: 'Referral from a Member' },
  { value: 'event', label: 'Industry Event / Conference' },
  { value: 'social', label: 'Social Media' },
  { value: 'other', label: 'Other' },
];

const BLANK_REF = {
  company: '',
  contact: '',
  email: '',
  phone: '',
  phoneCountryCode: null
};

const INITIAL = {
  companyName: '',
  companyAddress: '',
  city: '',
  country: null,
  postalCode: '',
  companyEmail: '',
  phone: '',
  phoneCountryCode: null,
  fax: '',
  website: '',
  yearEstablished: null,
  employees: null,
  contactName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
  contactPhoneCountryCode: null,
  annualRevenue: null,
  shipmentVolume: null,
  tradeRoutes: '',
  membershipPlan: null,
  services: [],
  iataCode: '',
  fiataCode: '',
  otherCert: null,
  refs: [
    { company: '', contact: '', email: '', phone: '', phoneCountryCode: null },
    { company: '', contact: '', email: '', phone: '', phoneCountryCode: null },
  ],
  howFound: null,
  howFoundOther: '',
  logo: null,
  tradeLicense: null,
  agreed: false,
};

const FieldError = ({ msg }) => msg
  ? <span className="ju-field-error">{msg}</span>
  : null;

const selectStyles = {
  control: (p, s) => ({
    ...p, minHeight: '48px', borderRadius: '8px',
    border: s.isFocused ? '1px solid var(--theme-primary)' : '1px solid var(--theme-border)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--theme-primary-light)' : 'none',
    backgroundColor: 'var(--theme-bg-input)', fontSize: '15px', cursor: 'pointer',
    '&:hover': { borderColor: 'var(--theme-primary)' },
  }),
  option: (p, s) => ({
    ...p, fontSize: '15px', cursor: 'pointer',
    backgroundColor: s.isSelected ? 'var(--theme-primary)' : s.isFocused ? 'var(--theme-primary-light)' : 'var(--theme-bg-dropdown)',
    color: s.isSelected ? '#fff' : 'var(--theme-text-primary)',
  }),
  menu: (p) => ({ ...p, borderRadius: '8px', border: '1px solid var(--theme-border)', backgroundColor: 'var(--theme-bg-dropdown)', zIndex: 9999 }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
  placeholder: (p) => ({ ...p, color: '#9ca3af', fontSize: '15px' }),
  singleValue: (p) => ({ ...p, color: 'var(--theme-text-primary)', fontSize: '15px' }),
  multiValue: (p) => ({ ...p, backgroundColor: 'var(--theme-primary-light)', borderRadius: '4px' }),
  multiValueLabel: (p) => ({ ...p, color: 'var(--theme-primary)', fontSize: '14px' }),
};

// Helper function to get user-friendly error messages (moved outside component to prevent re-renders)
const getErrorMessage = (errorPayload) => {
  if (!errorPayload) {
    return { message: 'An unexpected error occurred. Please try again.', title: 'Error' };
  }

  const message = errorPayload.message || '';
  const status = errorPayload.status;
  const errorType = errorPayload.nmm_error_type;

  // Network errors
  if (status === 0) {
    return {
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      title: 'Connection Error'
    };
  }

  // Server errors
  if (status >= 500) {
    return {
      message: 'A server error occurred. Please try again in a few moments.',
      title: 'Server Error'
    };
  }

  // Specific NMM error types
  if (errorType === 'email_exists') {
    return {
      message: message || 'This email is already registered in our system. Please use a different email address.',
      title: 'Email Already Registered'
    };
  }

  // OTP related errors
  if (message.toLowerCase().includes('otp')) {
    if (message.includes('invalid') || message.includes('expired')) {
      return {
        message: 'The verification code you entered is invalid or has expired. Please check your email and try again.',
        title: 'Invalid Verification Code'
      };
    }
    return {
      message: 'There was an issue with the verification code. Please try again.',
      title: 'Verification Error'
    };
  }

  // Logo related errors
  if (message.toLowerCase().includes('logo')) {
    return {
      message: 'Company logo is required for registration. Please upload your logo and try again.',
      title: 'Logo Required'
    };
  }

  // Referral errors
  if (message.toLowerCase().includes('referral')) {
    return {
      message: 'The referral code provided is invalid. Please check and try again.',
      title: 'Invalid Referral Code'
    };
  }

  // Authentication/permission errors
  if (status === 401 || status === 403) {
    return {
      message: 'Authentication failed. Please refresh the page and try again.',
      title: 'Authentication Error'
    };
  }

  // Rate limiting
  if (status === 429) {
    return {
      message: 'Too many requests. Please wait a moment before trying again.',
      title: 'Rate Limited'
    };
  }

  // Default to the provided message or a generic one
  return {
    message: message || 'Form submission failed. Please try again.',
    title: 'Submission Failed'
  };
};

const MembershipApplication = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { countries, countryCode } = useSelector((state) => state.country);
  const {
    loading,
    error: apiError,
    successMessage
  } = useSelector(s => s.JoinUsSlice);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);
  const [lastToastId, setLastToastId] = useState(null);
  const [nmmSteps, setNmmSteps] = useState({
    step: 'idle', // idle, sending_otp, completed
    message: ''
  });
  const toastTimeoutRef = useRef(null);
  const lastToastTimeRef = useRef(0);
  const submitTimeoutRef = useRef(null);

  // Toast helper functions with proper deduplication and throttling
  const showToast = (message, type = 'success', title = null) => {
    const now = Date.now();
    const toastKey = `${message}-${type}`;

    // Prevent duplicate toasts within 1 second
    if (lastToastId === toastKey && now - lastToastTimeRef.current < 1000) {
      console.log('🍞 Preventing duplicate toast (throttled):', message);
      return;
    }

    // Clear existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }

    console.log('🍞 Showing toast:', { message, type, title });

    const toastData = {
      id: `${toastKey}-${now}`,
      message,
      type,
      title: title || (type === 'success' ? 'Success' : type === 'error' ? 'Error' : type === 'warning' ? 'Warning' : 'Info')
    };

    setToast(toastData);
    setLastToastId(toastKey);
    lastToastTimeRef.current = now;

    // Auto-hide after 5 seconds
    toastTimeoutRef.current = setTimeout(() => {
      console.log('🍞 Auto-hiding toast');
      setToast(null);
      setLastToastId(null);
      toastTimeoutRef.current = null;
    }, 5000);
  };


  useEffect(() => {
    dispatch(fetchCountryListApi());
    dispatch(fetchCountryCodeListApi());
  }, []);

  const hideToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
    setLastToastId(null);
  };

  // Clear timers and state on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      setToast(null);
      setLastToastId(null);
    };
  }, []);

  // Get selected plan from URL params or location state
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');

    if (planParam) {
      const selectedPlan = PLAN_OPTIONS.find(p => p.value === planParam);
      if (selectedPlan) {
        setForm(prev => ({ ...prev, membershipPlan: selectedPlan }));
      }
    }
  }, [location]);

  // Clear toast when component unmounts or when form resets
  useEffect(() => {
    return () => {
      setToast(null);
      setLastToastId(null);
    };
  }, []);

  // Handle Redux state changes for toast notifications
  useEffect(() => {
    if (apiError && nmmSteps.step !== 'idle' && nmmSteps.step !== 'completed') {
      const { message: errorMessage, title: errorTitle } = getErrorMessage({ message: apiError });
      showToast(errorMessage, 'error', errorTitle);
    }
  }, [apiError, nmmSteps.step]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const setRef = (i, field, value) => {
    const refs = [...form.refs];
    refs[i] = { ...refs[i], [field]: value };
    setForm(p => ({ ...p, refs }));
  };

  const validate = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = 'Required';
    if (!form.companyAddress.trim()) e.companyAddress = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.country) e.country = 'Required';
    if (!form.postalCode.trim()) e.postalCode = 'Required';
    if (!form.companyEmail.trim()) e.companyEmail = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.companyEmail)) e.companyEmail = 'Invalid email';
    if (!form.website.trim()) e.website = 'Required';
    if (!form.yearEstablished) e.yearEstablished = 'Required';
    if (!form.employees) e.employees = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.phoneCountryCode) e.phoneCountryCode = 'Required';
    if (!form.contactName.trim()) e.contactName = 'Required';
    if (!form.contactTitle.trim()) e.contactTitle = 'Required';
    if (!form.contactEmail.trim()) e.contactEmail = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.contactEmail)) e.contactEmail = 'Invalid email';
    if (!form.contactPhone.trim()) e.contactPhone = 'Required';
    if (!form.contactPhoneCountryCode) e.contactPhoneCountryCode = 'Required';
    if (!form.annualRevenue) e.annualRevenue = 'Required';
    if (!form.membershipPlan) e.membershipPlan = 'Required';
    if (!form.services.length) e.services = 'Select at least one service';
    if (!form.iataCode.trim() && !form.fiataCode.trim() && !form.otherCert) {
      e.certification = 'At least one certification is required (IATA Code, FIATA Code, or upload other certificate)';
    }
    if (!form.logo) e.logo = 'Company logo is required';
    if (!form.tradeLicense) e.tradeLicense = 'Trade license is required';
    form.refs.slice(0, 2).forEach((r, i) => {
      if (!(r.company || '').trim()) e[`ref${i}company`] = 'Required';
      if (!(r.contact || '').trim()) e[`ref${i}contact`] = 'Required';
      if (!(r.phone || '').trim()) e[`ref${i}phone`] = 'Required';
      if (!(r.email || '').trim()) e[`ref${i}email`] = 'Required';
    });
    if (!form.agreed) e.agreed = 'You must accept the terms';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent rapid successive form submissions
    if (submitTimeoutRef.current || loading || nmmSteps.step !== 'idle') {
      console.log('🚫 Preventing duplicate form submission');
      return;
    }

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      console.log('❌ Validation errors:', errs);
      showToast(`Please fill all required fields (${Object.keys(errs).length} missing)`, 'error', 'Validation Error');
      // Use setTimeout to allow React to render the error elements first
      setTimeout(() => {
        document.querySelector('.ju-field-error')?.closest('.ju-form-group')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    // Set a timeout to prevent rapid resubmissions
    submitTimeoutRef.current = setTimeout(() => {
      submitTimeoutRef.current = null;
    }, 2000); // 2 second cooldown

    const payload = new FormData();

    // Section 1: Company
    payload.append('companyName', form.companyName);
    payload.append('companyAddress', form.companyAddress);
    payload.append('city', form.city);
    payload.append('country', form.country?.value ?? '');
    payload.append('postalCode', form.postalCode);
    payload.append('companyEmail', form.companyEmail);
    payload.append('phone', form.phone);
    payload.append('phoneCountryCode', form.phoneCountryCode?.value ?? '');
    payload.append('fax', form.fax);
    payload.append('website', form.website);
    payload.append('yearEstablished', form.yearEstablished ? dayjs(form.yearEstablished).format('YYYY-MM-DD') : '');
    payload.append('employees', form.employees?.value ?? '');

    // Section 2: Contact
    payload.append('contactName', form.contactName);
    payload.append('contactTitle', form.contactTitle);
    payload.append('contactEmail', form.contactEmail);
    payload.append('contactPhone', form.contactPhone);
    payload.append('contactPhoneCountryCode', form.contactPhoneCountryCode?.value ?? '');

    // Section 3: Business
    payload.append('annualRevenue', form.annualRevenue?.value ?? '');
    payload.append('shipmentVolume', form.shipmentVolume?.value ?? '');
    payload.append('tradeRoutes', form.tradeRoutes);
    payload.append('membershipPlan', form.membershipPlan?.value ?? '');

    // Section 4: Services
    payload.append('services', form.services.map(s => s.value).join(', '));

    // Section 5: Certifications
    payload.append('iataCode', form.iataCode);
    payload.append('fiataCode', form.fiataCode);
    if (form.otherCert) payload.append('otherCert', form.otherCert);

    // Section 6: References (as JSON string)
    payload.append('refs', JSON.stringify(form.refs));

    // Section 7: Marketing
    payload.append('howFound', form.howFound?.value ?? '');
    payload.append('howFoundOther', form.howFoundOther);

    // Section 8: Files
    if (form.logo) payload.append('logo', form.logo);
    if (form.tradeLicense) payload.append('tradeLicense', form.tradeLicense);

    // Submit form - direct registration (no OTP)
    setNmmSteps({ step: 'sending_otp', message: 'Submitting your application...' });

    const result = await dispatch(JoinUsSubmitApi(payload));
    console.log('📤 Form submission result:', result);

    if (JoinUsSubmitApi.rejected.match(result)) {
      console.log('❌ Form submission rejected:', result);
      const errorPayload = result.payload;

      // Clear the submission timeout since we can try again
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }

      // Handle specific cases that need field highlighting
      if (errorPayload?.nmm_error_type === 'email_exists') {
        setErrors(prev => ({ ...prev, companyEmail: 'This email is already registered in our system' }));
        setTimeout(() => {
          document.querySelector('input[type="email"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }

      setNmmSteps({ step: 'idle', message: '' });
      return;
    }

    if (!JoinUsSubmitApi.fulfilled.match(result)) {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      setNmmSteps({ step: 'idle', message: '' });
      return;
    }

    // Success - application registered
    console.log('✅ Application submitted successfully');
    setSubmitted(true);
    setNmmSteps({ step: 'completed', message: 'Application completed successfully' });
    showToast('Application submitted successfully!', 'success', 'Success');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Toast component
  const Toast = ({ toast, onClose }) => {
    console.log('🍞 Toast component render:', toast);
    if (!toast) return null;

    const getIcon = (type) => {
      switch (type) {
        case 'success':
          return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          );
        case 'error':
          return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          );
        case 'warning':
          return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          );
        case 'info':
        default:
          return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="12" x2="12" y2="16" /><line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          );
      }
    };

    return (
      <div className={`ju-toast ${toast.type}`}>
        <div className="ju-toast-header">
          <div className={`ju-toast-icon ${toast.type}`}>
            {getIcon(toast.type)}
          </div>
          <div className="ju-toast-title">{toast.title}</div>
          <button className="ju-toast-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="ju-toast-body">
          {toast.message}
        </div>
        <div className="ju-toast-progress">
          <div className={`ju-toast-progress-bar ${toast.type}`}></div>
        </div>
      </div>
    );
  };

  if (submitted) return (
    <div className="ju-page ju-success-page">
      <div className="ju-success-container compact">
        <div className="ju-success-animation">
          <div className="ju-success-circle">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ju-success-icon">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
        </div>

        <div className="ju-success-content">
          <h1 className="ju-success-title">Application Submitted!</h1>
          <p className="ju-success-message">
            Thank you for applying to join the Elite Freight Forwarders Network. Your application has been received and processed successfully.
          </p>

          <div className="ju-success-badges">
            <div className="ju-success-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Application Submitted
            </div>
          </div>

          <div className="ju-success-info">
            <div className="ju-info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="ju-info-text">
              <strong>What's next?</strong> Our team will review your application and contact you within 10 business days.
            </div>
          </div>

          <div className="ju-success-actions">
            <Link to="/" className="ju-btn-primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9,22 9,12 15,12 15,22" />
              </svg>
              Return to Homepage
            </Link>

            <Link to="/join-us" className="ju-btn-secondary ju-btn-outlined">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ju-page">
      {/* Hero */}
      <section className="ju-hero">
        <div className="container">
          <div className="ju-hero-content">
            <span className="ju-eyebrow">Membership Application</span>
            <h1 className="ju-hero-title">Complete Your Application</h1>
            <p className="ju-hero-sub">Fill out the form below to join the Elite Freight Forwarders Network</p>
          </div>
        </div>
      </section>

      {/* Toast Notifications */}
      <Toast toast={toast} onClose={hideToast} />

      {/* Form */}
      <section className="ju-form-section">
        <div className="container">
          <form className="ju-form" onSubmit={handleSubmit} noValidate>

            {apiError && nmmSteps.step === 'idle' && (
              <div className="ju-api-error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {apiError}
              </div>
            )}

            {/* 1. Company Information */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">01</span>
                <h2 className="ju-card-title">Company Information</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Company Name *</label>
                    <input value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Legal company name" className={errors.companyName ? 'error' : ''} />
                    <FieldError msg={errors.companyName} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Company Email * <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}></span></label>
                    <input type="email" value={form.companyEmail} onChange={e => set('companyEmail', e.target.value)} placeholder="info@yourcompany.com" className={errors.companyEmail ? 'error' : ''} />
                    <FieldError msg={errors.companyEmail} />
                  </div>
                </div>
                <div className="ju-form-group">
                  <label>Company Address *</label>
                  <input value={form.companyAddress} onChange={e => set('companyAddress', e.target.value)} placeholder="Street address" className={errors.companyAddress ? 'error' : ''} />
                  <FieldError msg={errors.companyAddress} />
                </div>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-3">
                    <label>City *</label>
                    <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" className={errors.city ? 'error' : ''} />
                    <FieldError msg={errors.city} />
                  </div>
                  <div className="ju-form-group ju-col-3">
                    <label>Country *</label>
                    <Select options={countries} value={form.country} onChange={v => set('country', v)}
                      styles={selectStyles} placeholder="Select country" isSearchable
                      menuPortalTarget={document.body} menuPosition="fixed"
                      className={errors.country ? 'select-error' : ''} />
                    <FieldError msg={errors.country} />
                  </div>
                  <div className="ju-form-group ju-col-3">
                    <label>Postal Code *</label>
                    <input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} placeholder="Postal / ZIP" className={errors.postalCode ? 'error' : ''} />
                    <FieldError msg={errors.postalCode} />
                  </div>
                </div>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Website *</label>
                    <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://yourcompany.com" className={errors.website ? 'error' : ''} />
                    <FieldError msg={errors.website} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Fax</label>
                    <input value={form.fax} onChange={e => set('fax', e.target.value)} placeholder="Fax number" />
                  </div>
                </div>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Phone *</label>
                    <div className="ju-phone-input">
                      <Select
                        options={countryCode}
                        value={form.phoneCountryCode}
                        onChange={v => set('phoneCountryCode', v)}
                        styles={selectStyles}
                        placeholder="Code"
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        className={`ju-country-code-select ${errors.phoneCountryCode ? 'select-error' : ''}`}
                      />
                      <input
                        value={form.phone}
                        onChange={e => set('phone', e.target.value)}
                        placeholder="4-123-4567"
                        className={errors.phone ? 'error' : ''}
                      />
                    </div>
                    <FieldError msg={errors.phone || errors.phoneCountryCode} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Number of Employees *</label>
                    <Select options={EMPLOYEE_OPTIONS} value={form.employees} onChange={v => set('employees', v)}
                      styles={selectStyles} placeholder="Select range" isSearchable={false}
                      menuPortalTarget={document.body} menuPosition="fixed"
                      className={errors.employees ? 'select-error' : ''} />
                    <FieldError msg={errors.employees} />
                  </div>
                </div>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Date Established *</label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={form.yearEstablished}
                        onChange={(newValue) => set('yearEstablished', newValue)}
                        format="DD-MM-YYYY"
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            placeholder: 'DD-MM-YYYY',
                            error: !!errors.yearEstablished,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                minHeight: '48px',
                                borderRadius: '8px',
                                fontSize: '15px',
                                backgroundColor: 'var(--theme-bg-input, #fff)',
                                '& fieldset': {
                                  borderColor: 'var(--theme-border, #e0e0e0)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'var(--theme-primary, #D4AF37)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'var(--theme-primary, #D4AF37)',
                                },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <FieldError msg={errors.yearEstablished} />
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Contact Person */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">02</span>
                <h2 className="ju-card-title">Contact Person</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Full Name *</label>
                    <input value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="Full name" className={errors.contactName ? 'error' : ''} />
                    <FieldError msg={errors.contactName} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Job Title *</label>
                    <input value={form.contactTitle} onChange={e => set('contactTitle', e.target.value)} placeholder="e.g. Managing Director" className={errors.contactTitle ? 'error' : ''} />
                    <FieldError msg={errors.contactTitle} />
                  </div>
                </div>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      value={form.contactEmail}
                      onChange={e => {
                        set('contactEmail', e.target.value);
                        // Clear email error when user changes email
                        if (errors.contactEmail) {
                          setErrors(prev => ({ ...prev, contactEmail: undefined }));
                        }
                      }}
                      placeholder="contact@company.com"
                      className={errors.contactEmail ? 'error' : ''}
                    />
                    <FieldError msg={errors.contactEmail} />
                    <div className="ju-field-note">
                      This email will be used for account registration and must not be previously registered.
                    </div>
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Direct Phone *</label>
                    <div className="ju-phone-input">
                      <Select
                        options={countryCode}
                        value={form.contactPhoneCountryCode}
                        onChange={v => set('contactPhoneCountryCode', v)}
                        styles={selectStyles}
                        placeholder="Code"
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        className={`ju-country-code-select ${errors.contactPhoneCountryCode ? 'select-error' : ''}`}
                      />
                      <input
                        value={form.contactPhone}
                        onChange={e => set('contactPhone', e.target.value)}
                        placeholder="50-123-4567"
                        className={errors.contactPhone ? 'error' : ''}
                      />
                    </div>
                    <FieldError msg={errors.contactPhone || errors.contactPhoneCountryCode} />
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Business Details */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">03</span>
                <h2 className="ju-card-title">Business Details</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Annual Revenue (USD) *</label>
                    <Select options={REVENUE_OPTIONS} value={form.annualRevenue} onChange={v => set('annualRevenue', v)}
                      styles={selectStyles} placeholder="Select range" isSearchable={false}
                      menuPortalTarget={document.body} menuPosition="fixed"
                      className={errors.annualRevenue ? 'select-error' : ''} />
                    <FieldError msg={errors.annualRevenue} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Monthly Shipment Volume</label>
                    <Select options={VOLUME_OPTIONS} value={form.shipmentVolume} onChange={v => set('shipmentVolume', v)}
                      styles={selectStyles} placeholder="Select range" isSearchable={false}
                      menuPortalTarget={document.body} menuPosition="fixed" />
                  </div>
                </div>
                <div className="ju-form-group">
                  <label>Main Trade Routes / Regions</label>
                  <textarea value={form.tradeRoutes} onChange={e => set('tradeRoutes', e.target.value)} rows="3" placeholder="Describe your main trade lanes and geographic coverage..." />
                </div>
                <div className="ju-form-group">
                  <label>Membership Plan *</label>
                  <Select options={PLAN_OPTIONS} value={form.membershipPlan} onChange={v => set('membershipPlan', v)}
                    styles={selectStyles} placeholder="Select a plan" isSearchable={false}
                    menuPortalTarget={document.body} menuPosition="fixed" />
                  <FieldError msg={errors.membershipPlan} />
                </div>
              </div>
            </div>

            {/* 4. Services */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">04</span>
                <h2 className="ju-card-title">Services Offered</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-form-group">
                  <label>Select all services your company provides *</label>
                  <Select options={SERVICES_OPTIONS} value={form.services} onChange={v => set('services', v)}
                    styles={selectStyles} placeholder="Select services..." isMulti
                    menuPortalTarget={document.body} menuPosition="fixed" />
                  <FieldError msg={errors.services} />
                </div>
              </div>
            </div>

            {/* 5. Certifications */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">05</span>
                <h2 className="ju-card-title">Certifications & Memberships</h2>
              </div>
              <div className="ju-card-body">
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>At least one of the following is required *</p>
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>IATA Code</label>
                    <input value={form.iataCode} onChange={e => set('iataCode', e.target.value)} placeholder="IATA agent code" className={errors.certification ? 'error' : ''} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>FIATA Code</label>
                    <input value={form.fiataCode} onChange={e => set('fiataCode', e.target.value)} placeholder="FIATA member code" className={errors.certification ? 'error' : ''} />
                  </div>
                </div>
                <div className="ju-form-group">
                  <label>Other Certification (Upload)</label>
                  <label className="ju-file-label">
                    <input type="file" accept=".pdf,.jpg,.png" onChange={e => set('otherCert', e.target.files[0])} />
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                    <span>{form.otherCert ? form.otherCert.name : 'Upload certificate (PDF, JPG, PNG)'}</span>
                  </label>
                </div>
                <FieldError msg={errors.certification} />
              </div>
            </div>

            {/* 6. References */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">06</span>
                <h2 className="ju-card-title">Business References</h2>
                <span className="ju-card-note">2 required</span>
              </div>
              <div className="ju-card-body">
                {form.refs.map((ref, i) => (
                  <div key={i} className="ju-ref-block">
                    <div className="ju-ref-header">
                      <span className="ju-ref-label">Reference {i + 1}</span>
                    </div>
                    <div className="ju-row">
                      <div className="ju-form-group ju-col-2">
                        <label>Company Name *</label>
                        <input value={ref.company} onChange={e => setRef(i, 'company', e.target.value)} placeholder="Company name" className={errors[`ref${i}company`] ? 'error' : ''} />
                        <FieldError msg={errors[`ref${i}company`]} />
                      </div>
                      <div className="ju-form-group ju-col-2">
                        <label>Contact Person *</label>
                        <input value={ref.contact} onChange={e => setRef(i, 'contact', e.target.value)} placeholder="Contact name" className={errors[`ref${i}contact`] ? 'error' : ''} />
                        <FieldError msg={errors[`ref${i}contact`]} />
                      </div>
                    </div>
                    <div className="ju-row">
                      <div className="ju-form-group ju-col-2">
                        <label>Email *</label>
                        <input type="email" value={ref.email} onChange={e => setRef(i, 'email', e.target.value)} placeholder="email@company.com" className={errors[`ref${i}email`] ? 'error' : ''} />
                        <FieldError msg={errors[`ref${i}email`]} />
                      </div>
                      <div className="ju-form-group ju-col-2">
                        <label>Phone *</label>
                        <div className="ju-phone-input">
                          <Select
                            options={countryCode}
                            value={ref.phoneCountryCode}
                            onChange={v => setRef(i, 'phoneCountryCode', v)}
                            styles={selectStyles}
                            placeholder="Code"
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            className={`ju-country-code-select ${errors[`ref${i}phoneCountryCode`] ? 'select-error' : ''}`}
                          />
                          <input
                            value={ref.phone}
                            onChange={e => setRef(i, 'phone', e.target.value)}
                            placeholder="4-333-4444"
                            className={errors[`ref${i}phone`] ? 'error' : ''}
                          />
                        </div>
                        <FieldError msg={errors[`ref${i}phone`]} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 7. Marketing */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">07</span>
                <h2 className="ju-card-title">How Did You Find Us?</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-form-group">
                  <Select options={HOW_OPTIONS} value={form.howFound} onChange={v => set('howFound', v)}
                    styles={selectStyles} placeholder="Select an option"
                    menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                {form.howFound?.value === 'other' && (
                  <div className="ju-form-group">
                    <label>Please specify</label>
                    <input value={form.howFoundOther} onChange={e => set('howFoundOther', e.target.value)} placeholder="Tell us more..." />
                  </div>
                )}
              </div>
            </div>

            {/* 8. Uploads */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">08</span>
                <h2 className="ju-card-title">Document Uploads</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-row">
                  <div className="ju-form-group ju-col-2">
                    <label>Company Logo *</label>
                    <label className={`ju-file-label ${errors.logo ? 'error' : ''}`}>
                      <input type="file" accept="image/*" onChange={e => set('logo', e.target.files[0])} />
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      <span>{form.logo ? form.logo.name : 'Upload logo (PNG, JPG)'}</span>
                    </label>
                    <FieldError msg={errors.logo} />
                  </div>
                  <div className="ju-form-group ju-col-2">
                    <label>Trade License / Registration *</label>
                    <label className="ju-file-label">
                      <input type="file" accept=".pdf,.jpg,.png" onChange={e => set('tradeLicense', e.target.files[0])} />
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      <span>{form.tradeLicense ? form.tradeLicense.name : 'Upload document (PDF, JPG, PNG)'}</span>
                    </label>
                    <FieldError msg={errors.tradeLicense} />
                  </div>
                </div>
              </div>
            </div>

            {/* 9. Agreement */}
            <div className="ju-card">
              <div className="ju-card-header">
                <span className="ju-card-num">09</span>
                <h2 className="ju-card-title">Declaration & Agreement</h2>
              </div>
              <div className="ju-card-body">
                <div className="ju-agreement-text">
                  <p>By submitting this application, I confirm that:</p>
                  <ul>
                    <li>All information provided is accurate and complete to the best of my knowledge.</li>
                    <li>I have the authority to submit this application on behalf of the company.</li>
                    <li>I agree to abide by EFFN's Code of Conduct and network standards.</li>
                    <li>I understand that membership is subject to approval and annual renewal.</li>
                    <li>I consent to EFFN contacting the references provided for verification.</li>
                  </ul>
                </div>
                <label className={`ju-checkbox-label ${errors.agreed ? 'error' : ''}`}>
                  <input type="checkbox" checked={form.agreed} onChange={e => set('agreed', e.target.checked)} />
                  <span>I have read and agree to the above declaration and EFFN's <Link to="/terms" target="_blank" rel="noreferrer">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>.</span>
                </label>
                <FieldError msg={errors.agreed} />
              </div>
            </div>

            {/* Submit */}
            <div className="ju-submit-row">

              {/* Progress Steps Display */}
              {nmmSteps.step !== 'idle' && (
                <div className="ju-progress-steps">
                  <div className="ju-progress-message">
                    <div className="ju-progress-icon">
                      {nmmSteps.step === 'completed' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      ) : (
                        <span className="ju-spinner" />
                      )}
                    </div>
                    <span>{nmmSteps.message}</span>
                  </div>

                  <div className="ju-progress-bar">
                    <div className="ju-progress-track">
                      <div
                        className="ju-progress-fill"
                        style={{
                          width: nmmSteps.step === 'sending_otp' ? '50%' :
                            nmmSteps.step === 'waiting_otp' ? '75%' :
                              nmmSteps.step === 'completing_registration' ? '90%' :
                                nmmSteps.step === 'completed' ? '100%' : '0%'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="ju-button-group">
                {nmmSteps.step === 'idle' ? (
                  <button
                    type="submit"
                    className="ju-btn-primary"
                    disabled={loading}
                  >
                    Submit Membership Application
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : nmmSteps.step === 'completed' && (
                  <button
                    type="button"
                    className="ju-btn-primary ju-btn-success"
                    disabled
                  >
                    Application Submitted!
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

          </form>
        </div>
      </section>

    </div>
  );
};

export default MembershipApplication;
